import gtm from 'eco-design-reliance/gtm';
import { useCallback, useEffect, useRef, useState } from 'react';
import './index.scss';

const ThemeChange: React.FC = () => {
    const [open, setOpen] = useState(true);
    // 切换视频逻辑
    const hasTriedPlayRefA = useRef(false);
    const hasTriedPlayRefB = useRef(false);
    const videoARef = useRef<HTMLVideoElement>(null);
    const videoBRef = useRef<HTMLVideoElement>(null);
    const waitFirstFrame = (video: HTMLVideoElement) =>
        new Promise<void>((resolve) => {
            let resolved = false;
            const done = () => {
                if (resolved) return;
                resolved = true;
                resolve();
            };
            // 首帧已存在
            if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                requestAnimationFrame(done);
                return;
            }
            const onLoadedData = () => {
                cleanup();
                requestAnimationFrame(done);
            };
            const onPlaying = () => {
                // 某些浏览器 playing 比 loadeddata 更早
                cleanup();
                requestAnimationFrame(done);
            };
            const cleanup = () => {
                video.removeEventListener('loadeddata', onLoadedData);
                video.removeEventListener('playing', onPlaying);
            };
            video.addEventListener('loadeddata', onLoadedData);
            video.addEventListener('playing', onPlaying);
            // 最终兜底（几乎不会走到）
            setTimeout(done, 2000);
        });

    const handleVideo1End = useCallback(async () => {
        const videoA = videoARef.current;
        const videoB = videoBRef.current;
        if (!videoA || !videoB) return;
        videoB.pause();
        videoB.currentTime = 0;

        const SAFE_DELTA = 0.04; // 冻结最后一帧
        videoA.currentTime = Math.max(0, videoA.duration - SAFE_DELTA);
        videoA.pause();

        // Video B 开始播放，首帧在 GPU 渲染管线中
        videoB.play().catch(() => console.log('播放失败'));

        // 等 Video B 首帧解码完成
        await waitFirstFrame(videoB);

        // Video A 隐藏，Video B 在下面就显示出来
        videoA.style.display = 'none';
    }, []);

    // 默认隐藏body滚动条
    useEffect(() => {
        if (typeof window === 'undefined') return;
        //document.body.style.overflow = 'hidden';
        const isOpenA = !(localStorage.getItem('new-home-theme-v3') === 'true');
        setOpen(isOpenA); // 保底 localeStorage
        gtm.push({
            __bth_only_ef: true,
            event: 'uaEvent',
            event_name: 'ThemeChangeDialog-open',
            __btn_type: 'common',
            __btn_id1: 'ThemeChangeDialog',
            __btn_id2: 'open',
        });
    }, []);

    // 关闭逻辑
    const dialogRef = useRef<HTMLDivElement>(null);
    const handleClose = () => {
        if (!dialogRef.current) return;
        dialogRef.current.classList.add('closing');
        //document.body.style.overflow = 'auto';
        document.cookie = 'new-home-theme-v3=true; path=/; max-age=864000'; // 10天，保证活动期间不会再次出现
        localStorage.setItem('new-home-theme-v3', 'true'); // 保底 localeStorage，避免浏览器禁用cookie
        // 动画结束后彻底关闭
        setTimeout(() => {
            setOpen(false);
        }, 1200);
        gtm.push({
            __bth_only_ef: true,
            event: 'uaEvent',
            event_name: 'ThemeChangeDialog-close',
            __btn_type: 'common',
            __btn_id1: 'ThemeChangeDialog',
            __btn_id2: 'close',
        });
    };
    if (!open) return null;
    return (
        <div className="ThemeChangeDialog" ref={dialogRef}>
            <video
                className="video1"
                autoPlay
                muted
                playsInline
                preload="auto"
                onPlay={() => {
                    if (!videoBRef.current) return;
                    videoBRef.current.pause();
                    videoBRef.current.currentTime = 0;
                }}
                onCanPlay={() => {
                    if (hasTriedPlayRefA.current) return;
                    hasTriedPlayRefA.current = true;
                    videoARef.current?.play().catch(() => console.log('播放失败'));
                }} // 防止浏览器阻止了一开始的 autoplay
                onEnded={handleVideo1End}
                style={{
                    // 写死在 dom 阶段，避免初始化闪
                    display: 'block',
                    width: '100vw',
                    height: '100vh',
                    objectFit: 'cover',
                }}
                ref={videoARef}
            >
                {/* <source src="https://websiteoss.ecoflow.com/upload/video/1765873960535/videoA.webm" type="video/webm" /> */}
                {/* <source src="https://websiteoss.ecoflow.com/upload/video/1765873717244/videoA.mp4" type="video/mp4" /> */}
                <source src="https://websiteoss.ecoflow.com/upload/video/1766566237585/video.mp4" type="video/mp4" />
                您的浏览器不支持视频播放
            </video>
            <video
                className="video2"
                muted
                loop
                playsInline
                preload="auto"
                ref={videoBRef}
                onCanPlay={() => {
                    if (hasTriedPlayRefB.current) return;
                    hasTriedPlayRefB.current = true;
                    videoBRef.current?.play().catch(() => console.log('播放失败'));
                }} // 防止浏览器阻止了一开始的 autoplay
                style={{
                    display: 'block',
                    width: '100vw',
                    height: '100vh',
                    objectFit: 'cover',
                }}
            >
                {/* <source src="https://websiteoss.ecoflow.com/upload/video/1765873960350/videoB.webm" type="video/webm" /> */}
                {/* <source src="https://websiteoss.ecoflow.com/upload/video/1765873717436/videoB.mp4" type="video/mp4" /> */}
                <source src="https://websiteoss.ecoflow.com/upload/video/1766567017173/video2.mp4" type="video/mp4" />
                您的浏览器不支持视频播放
            </video>

            <div className="newTheme-dialog-info">
                <div className="newTheme-dialog-info-title">New Year, New Energy</div>
                <div className="newTheme-dialog-info-desc">
                    {`For 2026, we're pushing the boundaries of what smart home energy can be and where it can take us.\nThat starts here, with a streamlined, easier to navigate website.\nHappy New Year from all of us at EcoFlow`}
                </div>
                {/* 关闭按钮 */}
                <div className="newTheme-dialog-info-btn" onClick={handleClose}>
                    Enter Now
                </div>
            </div>
        </div>
    );
};

export default ThemeChange;
